/**
 * Описание ноды в том виде, в каком его отдаёт служба
 * (`GET /api/v2/node-definitions`). Редактор не знает ни одной ноды по
 * имени: и палитра, и форма параметров строятся по этим описаниям
 * (FR-NODE-001), иначе каждая новая нода в службе требовала бы правки
 * админки.
 */

import type { ItemView } from './DataPanel.js'

export interface ParameterOptionView {
  value: string | number | boolean
  label: string
}

export type ParameterType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'options'
  | 'multiOptions'
  | 'json'
  | 'dateTime'
  | 'collection'
  | 'credentials'
  | 'instance'
  | 'workflow'

export interface NodeParameterView {
  name: string
  displayName: string
  type: ParameterType
  description?: string
  required?: boolean
  default?: unknown
  options?: readonly ParameterOptionView[]
  /** Показывать параметр, только когда другие параметры имеют указанные значения. */
  displayIf?: Readonly<Record<string, readonly unknown[]>>
  placeholder?: string
}

export interface PortView {
  name: string
  displayName: string
  portType?: 'main' | 'error'
}

export interface NodeDefinitionView {
  type: string
  version: number
  displayName: string
  group: string
  description: string
  icon?: string
  inputs: readonly PortView[]
  outputs: readonly PortView[]
  parameters: readonly NodeParameterView[]
  credentialTypes?: readonly string[]
  /**
   * Образец того, что узел отдаёт. Объявляют его триггеры: пробный прогон
   * наполняет им первый шаг, иначе сценарий, который начинается с
   * входящего сообщения, нечем было проверить — прогон падал на первом же
   * `{{ $json.text }}`.
   *
   * Редактор показывает образец до первого прогона: имя поля, которое
   * видно на экране, не набирают наугад.
   */
  sampleOutput?: readonly Record<string, unknown>[]
  /**
   * Канал, к номерам которого относится узел. По нему сужается список
   * номеров в параметре типа `instance`: номер другого канала выбрать
   * можно, но опубликовать нельзя (FR-PIPE-042), и предлагать его —
   * вести человека в тупик.
   */
  channelProvider?: string
}

/**
 * Образец выхода узла как элементы панели данных. Пустой список, когда
 * образца нет: у большинства узлов его и не будет — что отдаёт «Задать
 * поля», зависит от того, что в него пришло, и выдумывать это нельзя.
 */
export function sampleItemsOf(definition: NodeDefinitionView | undefined): ItemView[] {
  return (definition?.sampleOutput ?? []).map((json) => ({ json }))
}

/**
 * Значение параметра с учётом значения по умолчанию. Умолчание считается
 * заданным — ровно так же на него смотрит валидатор службы, и расхождение
 * означало бы форму, которая требует ввести то, что уже задано.
 *
 * Читается только собственное поле: `values['constructor']` иначе вернул бы
 * функцию из прототипа.
 */
export function effectiveValue(
  parameter: NodeParameterView | undefined,
  values: Readonly<Record<string, unknown>>,
): unknown {
  if (parameter === undefined) return undefined
  if (Object.prototype.hasOwnProperty.call(values, parameter.name)) return values[parameter.name]
  return parameter.default
}

/**
 * Параметр виден, когда все условия `displayIf` выполнены. Условие
 * сверяется с действующим значением, а не с введённым: у только что
 * добавленной ноды параметров нет вовсе, и без учёта умолчаний форма
 * показала бы поля, которые к её настройкам отношения не имеют.
 */
export function isVisible(
  parameter: NodeParameterView,
  values: Readonly<Record<string, unknown>>,
  all: readonly NodeParameterView[],
): boolean {
  const conditions = parameter.displayIf
  if (conditions === undefined) return true

  return Object.entries(conditions).every(([name, allowed]) => {
    const current = effectiveValue(
      all.find((candidate) => candidate.name === name),
      values,
    )
    return allowed.some((expected) => Object.is(expected, current))
  })
}

export function visibleParameters(
  definition: NodeDefinitionView,
  values: Readonly<Record<string, unknown>>,
): NodeParameterView[] {
  return definition.parameters.filter((parameter) =>
    isVisible(parameter, values, definition.parameters),
  )
}
